import {
  convertGroupStringsToGroupIds,
  getGroupsRestrictions,
  groupsContainLockedAndRegularChannels,
} from '@disco/utils/groups'
import { getTracksWithNestedTracks } from '@disco/utils/tracks'
import { createSelector } from 'reselect'
import type { ActionRestrictions, Group, GroupLockSettings } from 'types/group'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import {
  getGroups,
  getGroupsFromExternalShare,
} from '@disco/state-management/selectors/groups'
import { getPlaylistTags } from '@disco/state-management/selectors/tags'
import { getTrack, getTracks } from '@disco/state-management/selectors/tracks'
const emptyArray = []
/**
 * Gets all playlists from the state.
 * @param state - The Redux state
 * @returns The playlists object keyed by playlist ID
 */
export const getPlaylists = (state: State): State['playlists'] =>
  state.playlists
/**
 * Gets a specific playlist by ID.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @param props.id - The playlist ID to retrieve
 * @returns The playlist object, or null/undefined if not found
 */
export const getPlaylist = (
  state: State,
  props: {
    id: PlaylistId
  },
): Playlist | null | undefined => state.playlists[props.id]

/**
 * Transforms a playlist by replacing track IDs with full track objects.
 * @param playlist - The playlist object with track IDs
 * @param tracks - The tracks object keyed by track ID
 * @returns Playlist with tracks populated as full objects
 */
const getFullPlaylist = (playlist, tracks) => {
  const playlistTracks = playlist.tracks
    ? playlist.tracks.map(id => tracks[id])
    : emptyArray
  return { ...playlist, tracks: playlistTracks }
}

/**
 * Gets a playlist with its tracks populated (instead of just track IDs).
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @returns The playlist with full track objects, or null if playlist not found
 */
export const getPlaylistWithTracks = createSelector(
  [getTracks, getPlaylist],
  (tracks, playlist) => (playlist ? getFullPlaylist(playlist, tracks) : null),
)
/**
 * Gets playlist tracks with nested track relationships resolved.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @returns Array of tracks with nested relationships, or empty array if playlist not found
 */
export const getPlaylistTracksWithNested = createSelector(
  [getPlaylistWithTracks],
  playlist => (playlist ? getTracksWithNestedTracks(playlist?.tracks) : []),
)
/**
 * Gets the tags associated with a playlist, with full tag objects.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @returns Array of tag objects with color and name properties
 */
export const getPlaylistTagsByPlaylistId = createSelector(
  [getPlaylist, getPlaylistTags],
  (playlist, playlistTags) =>
    (playlist?.tags ?? []).map(tagName => {
      const tag = playlistTags.find(tag => tag.name === tagName)
      return (
        tag ?? {
          color: null,
          name: tagName,
        }
      )
    }),
)
/**
 * Gets multiple playlists by their IDs.
 * @param state - The Redux state
 * @param props - Object containing array of playlist IDs
 * @param props.ids - Array of playlist IDs to retrieve
 * @returns Array of playlist objects (may contain null/undefined for missing playlists)
 */
export const getPlaylistsByIds = createSelector(
  [getPlaylists, (_state: State, { ids }: { ids: PlaylistId[] }) => ids],
  (playlists, ids) => ids.map(id => playlists[id]),
)

/**
 * Gets the lock settings for a playlist based on its associated groups.
 * @param playlist - The playlist object
 * @param groups - Array of group objects
 * @returns Group lock settings for the playlist
 */
const getPlaylistLockSettings = (
  playlist: Playlist,
  groups: Group[],
): GroupLockSettings => {
  const playlistGroups = convertGroupStringsToGroupIds(playlist.groups)
  return getGroupsRestrictions(groups, playlistGroups)
}

/**
 * Gets action restrictions for a playlist based on its group lock settings.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @param props.id - The playlist ID to get restrictions for
 * @returns Action restrictions object indicating what actions are disabled
 */
const getPlaylistRestrictions: (
  state: State,
  props: {
    id: PlaylistId
  },
) => ActionRestrictions = createSelector(
  [getPlaylist, getGroups],
  (playlist, groups) => {
    const locks = playlist ? getPlaylistLockSettings(playlist, groups) : null
    return {
      disableDelete: locks?.lock_content ?? false,
    }
  },
)
/**
 * Factory function that creates a playlist restrictions selector.
 * @returns A selector function for getting playlist restrictions
 */
export const makeGetPlaylistRestrictions = (): typeof getPlaylistRestrictions =>
  getPlaylistRestrictions
/**
 * Checks if a locked playlist belongs to regular (non-locked) groups.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @param props.id - The playlist ID to check
 * @returns True if the locked playlist belongs to regular groups, false otherwise
 */
const getIfLockedPlaylistBelongsToRegularGroups: (
  state: State,
  props: {
    id: PlaylistId
  },
) => boolean = createSelector([getPlaylist, getGroups], (playlist, groups) => {
  if (!playlist) return false
  const playlistGroups = convertGroupStringsToGroupIds(playlist.groups)
  return groupsContainLockedAndRegularChannels(groups, playlistGroups)
})
/**
 * Factory function that creates a selector to check if a locked playlist belongs to regular groups.
 * @returns A selector function for checking locked playlist group membership
 */
export const makeGetIfLockedPlaylistBelongsToRegularGroups =
  (): typeof getIfLockedPlaylistBelongsToRegularGroups =>
    getIfLockedPlaylistBelongsToRegularGroups
/**
 * Checks if a playlist belongs to a channel shared to the current business by
 * another business. Channels the business owns and shares out don't count.
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @param props.id - The playlist ID to check
 * @returns True if any of the playlist's groups is a received shared channel
 */
export const getIsPlaylistInReceivedSharedChannel = createSelector(
  [getPlaylist, getGroupsFromExternalShare],
  (playlist, receivedGroups): boolean => {
    if (!playlist || receivedGroups.length === 0) return false
    const playlistGroupIds = new Set(
      convertGroupStringsToGroupIds(playlist.groups ?? []),
    )
    return receivedGroups.some(group => playlistGroupIds.has(group.id))
  },
)
/**
 * Gets the IDs of playlists that are allowed to be removed (not locked).
 * @param state - The Redux state
 * @param props - Object containing array of playlist IDs to check
 * @param props.ids - Array of playlist IDs to filter
 * @returns Array of playlist IDs that can be removed
 */
export const getPlaylistIdsAllowedForRemoval: (
  state: State,
  props: {
    ids: PlaylistId[]
  },
) => PlaylistId[] = createSelector(
  [getPlaylistsByIds, getGroups],
  (playlists, groups) => {
    const result = playlists
      .filter(playlist => {
        if (!playlist) return false
        const locks = getPlaylistLockSettings(playlist, groups)
        return !locks?.lock_content
      })
      .map(p => p?.playlist_id)
      .filter(Boolean)
    return result
  },
)
/**
 * Factory function that creates a selector to get all playlists that contain a specific track.
 * @returns A selector function that returns playlists containing the specified track
 */
export const makeGetTrackPlaylists = (): ((...args: Array<any>) => any) =>
  createSelector([getTrack, getPlaylists], (track, playlists) =>
    (track?.playlists || []).map(id => playlists[id]),
  )
/**
 * Gets all curated playlists from the state.
 * @param state - The Redux state
 * @returns The curated playlists object
 */
export const getCuratedPlaylists = (state: State): State['playlistsCurated'] =>
  state.playlistsCurated
